/* eslint-disable @typescript-eslint/no-explicit-any */
// Moved from apps/web/src/engine (#333), where `any` is permitted; this package
// is stricter. Disabled at the top rather than rewritten: same input as ./messaging: unvalidated stored JSON, typed on the way out,
// and retyping it inside an access-control change would mix a refactor into a
// move whose whole value is that it changed nothing. Same treatment as
// banepadResolveCourt.ts and activityRegistration.ts.
// ─────────────────────────────────────────────────────────────────────
// MESSAGING — dynamic club groups + member preferences.
//
// Every club a member belongs to gets a set of AUTOMATIC groups:
//   • «Nivå N · <klubb>» — one per ELO level; you are in the group for the
//     level you currently hold, and move with your rating (membership is
//     DERIVED from the player list on every read, never stored).
//   • «Alle · <klubb>» — everyone in the club.
//
// Members can mute a group (no unread badge) or leave the club's automatic
// groups altogether (all of them at once — not one level in isolation).
// Preferences live inside the messaging blob (state.prefs), newest-wins on
// merge.
//
// Clubs are passed in as a list ({ id, name }) so the same code works when a
// member belongs to several clubs (roles/memberships are the next step); today
// the list holds the one club this install serves.
// ─────────────────────────────────────────────────────────────────────

import { getEloLevel } from './eloLevels.js';
import {
  CLUB_SENDER_ID,
  conversationById,
  conversationsFor,
  lastMessageOf,
  unreadCount,
  type Conversation,
  type DynamicRule,
  type MemberPrefs,
  type MessagingState,
} from './messaging.js';

export interface ClubRef {
  id: string;
  name: string;
}

export const DYNAMIC_PREFIX = 'dyn:';

const sid = (v: unknown) => (v == null ? '' : String(v));

/** Deterministic id — the same rule always maps to the same conversation. */
export function dynamicConversationId(rule: DynamicRule): string {
  return rule.type === 'all'
    ? `${DYNAMIC_PREFIX}${rule.clubId}:all`
    : `${DYNAMIC_PREFIX}${rule.clubId}:level:${rule.level}`;
}

/** Parse a dynamic conversation id back to its rule (null for anything else). */
export function dynamicRuleOf(id: unknown): DynamicRule | null {
  const s = sid(id);
  if (!s.startsWith(DYNAMIC_PREFIX)) return null;
  const parts = s.slice(DYNAMIC_PREFIX.length).split(':');
  if (parts.length === 2 && parts[1] === 'all' && parts[0])
    return { type: 'all', clubId: parts[0] };
  if (parts.length === 3 && parts[1] === 'level' && parts[0]) {
    const level = Number(parts[2]);
    if (Number.isInteger(level) && level > 0) return { type: 'level', clubId: parts[0], level };
  }
  return null;
}

/** The ELO level (1–9) a player currently holds. */
export function memberLevel(player: any): number {
  return getEloLevel(Math.round(Number(player?.elo) || 0)).level;
}

/** Who is in a dynamic group right now — derived from the player list. */
export function dynamicMembersOf(rule: DynamicRule, players: any[]): string[] {
  const list = (players || []).filter((p) => p?.id != null);
  const picked = rule.type === 'all' ? list : list.filter((p) => memberLevel(p) === rule.level);
  return picked.map((p) => sid(p.id));
}

/** «Nivå 4 · Smaalenene Padelklubb» / «Alle · Smaalenene Padelklubb». */
export function dynamicTitle(rule: DynamicRule, clubs: ClubRef[]): string {
  const club = clubs.find((c) => c.id === rule.clubId)?.name || 'Klubben';
  return rule.type === 'all' ? `Alle · ${club}` : `Nivå ${rule.level} · ${club}`;
}

// ─── preferences ───

const EMPTY_PREFS: MemberPrefs = { muted: [], leftDynamic: [], disabled: false, updatedAt: 0 };

export function prefsOf(state: MessagingState, memberId: unknown): MemberPrefs {
  return state.prefs?.[sid(memberId)] || EMPTY_PREFS;
}

export function isMuted(
  state: MessagingState,
  memberId: unknown,
  conversationId: unknown,
): boolean {
  return prefsOf(state, memberId).muted.includes(sid(conversationId));
}

export function hasLeftDynamic(state: MessagingState, memberId: unknown, clubId: unknown): boolean {
  return prefsOf(state, memberId).leftDynamic.includes(sid(clubId));
}

function withPrefs(
  state: MessagingState,
  memberId: unknown,
  patch: (p: MemberPrefs) => MemberPrefs,
  now: number,
): MessagingState {
  const id = sid(memberId);
  const next = { ...patch(prefsOf(state, id)), updatedAt: now };
  return { ...state, prefs: { ...(state.prefs || {}), [id]: next } };
}

/** Has the member switched the messaging module off? */
export function isMessagingDisabled(state: MessagingState, memberId: unknown): boolean {
  return prefsOf(state, memberId).disabled;
}

/** Switch the whole messaging module off / on for one member. */
export function setMessagingDisabled(
  state: MessagingState,
  memberId: unknown,
  disabled: boolean,
  now: number,
): MessagingState {
  return withPrefs(state, memberId, (p) => ({ ...p, disabled }), now);
}

/** The players who take part in messaging — those who switched it off are invisible. */
export function messagingPlayers(state: MessagingState, players: any[]): any[] {
  return (players || []).filter((p) => p?.id != null && !isMessagingDisabled(state, p.id));
}

/**
 * A dm cannot be written to while the counterpart has messaging switched off.
 * Returns that member's id, or null when the conversation is open for writing.
 */
export function conversationBlockedBy(
  state: MessagingState,
  conversation: Conversation,
  viewerId: unknown,
): string | null {
  // Two-party conversations only: a dm, or the club/court ↔ member pair.
  if (conversation.kind !== 'dm' && conversation.kind !== 'club') return null;
  const other = conversation.participantIds.find((p) => p !== sid(viewerId));
  return other && isMessagingDisabled(state, other) ? other : null;
}

/** Mute / unmute one conversation for one member. */
export function setMuted(
  state: MessagingState,
  memberId: unknown,
  conversationId: unknown,
  muted: boolean,
  now: number,
): MessagingState {
  const cid = sid(conversationId);
  return withPrefs(
    state,
    memberId,
    (p) => ({
      ...p,
      muted: muted ? [...new Set([...p.muted, cid])] : p.muted.filter((x) => x !== cid),
    }),
    now,
  );
}

/** Leave (or rejoin) ALL of a club's automatic groups. */
export function setLeftDynamic(
  state: MessagingState,
  memberId: unknown,
  clubId: unknown,
  left: boolean,
  now: number,
): MessagingState {
  const c = sid(clubId);
  return withPrefs(
    state,
    memberId,
    (p) => ({
      ...p,
      leftDynamic: left
        ? [...new Set([...p.leftDynamic, c])]
        : p.leftDynamic.filter((x) => x !== c),
    }),
    now,
  );
}

// ─── conversations ───

/** The stored record behind a dynamic group (created on first use). */
export function ensureDynamicConversation(
  state: MessagingState,
  rule: DynamicRule,
  now: number,
): { state: MessagingState; conversation: Conversation } {
  const id = dynamicConversationId(rule);
  const existing = conversationById(state, id);
  if (existing) return { state, conversation: existing };
  const conversation: Conversation = {
    id,
    kind: 'dynamic',
    participantIds: [],
    title: null,
    createdAt: now,
    createdBy: 'system',
    lastMessageAt: 0,
    readAt: {},
    rule,
  };
  return {
    state: { ...state, conversations: [...state.conversations, conversation] },
    conversation,
  };
}

export interface GroupContext {
  players: any[];
  clubs: ClubRef[];
}

/** The rules a member is in right now: per club «Alle» + their own level. */
export function dynamicRulesFor(member: any, clubs: ClubRef[]): DynamicRule[] {
  if (!member) return [];
  const level = memberLevel(member);
  return clubs.flatMap((c) => [
    { type: 'all', clubId: c.id } as DynamicRule,
    { type: 'level', clubId: c.id, level } as DynamicRule,
  ]);
}

/**
 * The member's automatic groups as (virtual) conversations: membership and
 * title derived now, read state and last activity taken from the stored record
 * when there is one. Clubs the member has left are skipped.
 */
export function dynamicGroupsFor(
  state: MessagingState,
  memberId: unknown,
  ctx: GroupContext,
): Conversation[] {
  const id = sid(memberId);
  if (isMessagingDisabled(state, id)) return [];
  const players = messagingPlayers(state, ctx.players);
  const member = players.find((p) => sid(p?.id) === id);
  if (!member) return [];
  const clubs = ctx.clubs.filter((c) => !hasLeftDynamic(state, id, c.id));
  return dynamicRulesFor(member, clubs).map((rule) => {
    const stored = conversationById(state, dynamicConversationId(rule));
    return {
      id: dynamicConversationId(rule),
      kind: 'dynamic',
      participantIds: dynamicMembersOf(rule, players),
      title: dynamicTitle(rule, ctx.clubs),
      createdAt: stored?.createdAt ?? 0,
      createdBy: 'system',
      // Activity comes from the messages themselves, never from a stored stamp.
      lastMessageAt: lastMessageOf(state, dynamicConversationId(rule))?.sentAt ?? 0,
      readAt: stored?.readAt ?? {},
      rule,
    };
  });
}

/**
 * Every automatic group of the given clubs, as the CLUB sees them: «Alle» plus
 * one group per level that currently has members. The club is not a member,
 * but may read and post in all of them (canPost).
 *
 * MEMBERS WHO LEFT ARE EXCLUDED. dynamicMembersOf derives membership from ELO
 * level alone — it has no access to prefs — so a member who opted out of the
 * club's automatic groups would still be counted here while dynamicGroupsFor
 * correctly hides the group from them. Delivery was never wrong; the COUNT
 * was, and the count is the whole point of this panel: it is what tells the
 * club how many people a message reaches. Reporting 39 when 3 have opted out
 * is a claim the club would act on.
 */
export function clubGroupsFor(
  state: MessagingState,
  clubs: ClubRef[],
  allPlayers: any[],
): Conversation[] {
  const players = messagingPlayers(state, allPlayers); // members who switched off are invisible
  // Levels are derived per club from the members who have NOT opted out, so a
  // level whose every member left does not appear as an empty group either.
  const rules: DynamicRule[] = clubs.flatMap((c) => {
    const receiving = (players || []).filter(
      (p) => p?.id != null && !hasLeftDynamic(state, p.id, c.id),
    );
    const levels = [...new Set(receiving.map(memberLevel))].sort((a, b) => a - b);
    return [
      { type: 'all', clubId: c.id } as DynamicRule,
      ...levels.map((level) => ({ type: 'level', clubId: c.id, level }) as DynamicRule),
    ];
  });
  return rules.map((rule) => {
    const stored = conversationById(state, dynamicConversationId(rule));
    return {
      id: dynamicConversationId(rule),
      kind: 'dynamic',
      participantIds: dynamicMembersOf(rule, players).filter(
        (pid) => !hasLeftDynamic(state, pid, rule.clubId),
      ),
      title: dynamicTitle(rule, clubs),
      createdAt: stored?.createdAt ?? 0,
      createdBy: 'system',
      // Activity comes from the messages themselves, never from a stored stamp.
      lastMessageAt: lastMessageOf(state, dynamicConversationId(rule))?.sentAt ?? 0,
      readAt: stored?.readAt ?? {},
      rule,
    };
  });
}

/** A member's whole inbox: their conversations + their automatic groups, newest activity first. */
export function inboxFor(
  state: MessagingState,
  memberId: unknown,
  ctx: GroupContext,
): Conversation[] {
  if (isMessagingDisabled(state, memberId)) return []; // switched off: nothing in, nothing out
  const own = conversationsFor(state, memberId).filter((c) => c.kind !== 'dynamic');
  return [...own, ...dynamicGroupsFor(state, memberId, ctx)].sort(
    (a, b) => b.lastMessageAt - a.lastMessageAt || a.id.localeCompare(b.id),
  );
}

/** Unread across the inbox, muted conversations excluded. */
export function inboxUnread(state: MessagingState, memberId: unknown, ctx: GroupContext): number {
  return inboxFor(state, memberId, ctx).reduce(
    (n, c) => (isMuted(state, memberId, c.id) ? n : n + unreadCount(state, c, memberId)),
    0,
  );
}

/** May this member post here? Dynamic groups check the derived membership. */
export function canPost(
  state: MessagingState,
  conversation: Conversation,
  memberId: unknown,
  ctx: GroupContext,
): boolean {
  const id = sid(memberId);
  if (isMessagingDisabled(state, id)) return false; // the club and courts can switch off too
  if (conversationBlockedBy(state, conversation, id)) return false;
  const rule = conversation.rule || dynamicRuleOf(conversation.id);
  if (rule) {
    if (id === CLUB_SENDER_ID) return true; // the club may post in all of its groups
    return (
      !hasLeftDynamic(state, id, rule.clubId) &&
      dynamicMembersOf(rule, messagingPlayers(state, ctx.players)).includes(id)
    );
  }
  return conversation.participantIds.includes(id);
}
